import { getContext, setContext } from 'svelte';
import {
  canDeleteComment,
  commentTextProblem,
  type CommentThread,
  type DrawingComment,
} from '$lib/core/model';
import { RemoteCommentStore, type CommentStore, type Comments } from '$lib/services/comment-store';
import type { EditorState } from './editor-state.svelte';

const FAILED = 'Could not reach the comments; try again';

/**
 * The open drawing's comments: loading them when the drawing changes or the window gains
 * focus, posting one about the comment tool's box, replying, closing, reopening and
 * deleting. Comments are stored apart from the drawing, so none of this touches its script,
 * its revisions or its undo history.
 */
export class CommentSession {
  threads = $state.raw<readonly CommentThread[]>([]);
  /** The signed-in person's id, which their own comments carry. */
  you = $state<string | null>(null);
  /** The text of the comment being written about the comment tool's box. */
  draft = $state('');
  /** The thread picked in the panel, whose box the canvas outlines. */
  selectedId = $state<string | null>(null);
  /** A one-line problem, such as text that cannot be posted or a request that failed. */
  problem = $state<string | null>(null);

  /** Open threads first, then closed ones, each in the order posted. */
  readonly ordered: readonly CommentThread[] = $derived([
    ...this.threads.filter((thread) => !thread.closed),
    ...this.threads.filter((thread) => thread.closed),
  ]);

  private drawingId: string | null = null;
  /** The zoom when the comment tool's box was drawn, posted with it. */
  private drawnZoom: number | undefined = undefined;

  constructor(
    private readonly editor: EditorState,
    private readonly confirm: (message: string, confirmLabel: string) => Promise<boolean>,
    /** Saves the open drawing if it is new, since only a stored drawing can hold comments. */
    private readonly saveDrawing: () => Promise<void>,
    private readonly store: CommentStore = new RemoteCommentStore(),
  ) {}

  /** Shows the comments of the drawing now open; the editor calls it when that changes. */
  async open(drawingId: string | null): Promise<void> {
    if (drawingId === this.drawingId) return;
    this.drawingId = drawingId;
    this.threads = [];
    this.selectedId = null;
    this.problem = null;
    await this.refresh();
  }

  /** Picks up replies made elsewhere, such as an agent's; the editor calls it on focus. */
  async refresh(): Promise<void> {
    const id = this.drawingId;
    if (!id) return;
    await this.run(id, () => this.store.load(id));
  }

  /** Notes the zoom a box was drawn at; the editor calls it as each box is marked. */
  boxMarked(zoom: number): void {
    this.drawnZoom = zoom;
  }

  /** Drops the comment being written: its box and its text. */
  cancel(): void {
    this.draft = '';
    this.editor.cancelTool();
  }

  /** Posts the draft about the comment tool's box, then clears both for the next one. */
  async post(): Promise<void> {
    const region = this.editor.commentRegion;
    if (!region) return;
    const problem = commentTextProblem(this.draft);
    if (problem) {
      this.problem = problem;
      return;
    }
    await this.saveDrawing();
    const id = this.editor.documentId;
    if (!id) {
      this.problem = 'Draw something before commenting on it';
      return;
    }
    if (id !== this.drawingId) await this.open(id);
    if (await this.run(id, () => this.store.start(id, region, this.draft, this.drawnZoom))) {
      this.draft = '';
      this.editor.cancelTool();
    }
  }

  /** Returns whether the reply was posted, so the panel knows to clear its text. */
  async reply(threadId: string, text: string): Promise<boolean> {
    const problem = commentTextProblem(text);
    if (problem) {
      this.problem = problem;
      return false;
    }
    const id = this.drawingId;
    return id !== null && (await this.run(id, () => this.store.reply(id, threadId, text)));
  }

  async setClosed(threadId: string, closed: boolean): Promise<void> {
    const id = this.drawingId;
    if (id) await this.run(id, () => this.store.setClosed(id, threadId, closed));
  }

  /** Deletes a comment after asking, since its text is erased for good. */
  async remove(thread: CommentThread, comment: DrawingComment): Promise<void> {
    const id = this.drawingId;
    if (!id) return;
    const whole = thread.comments[0]?.id === comment.id;
    const message = whole
      ? 'Delete this comment and every reply to it? This cannot be undone.'
      : 'Delete this reply? This cannot be undone.';
    if (!(await this.confirm(message, 'Delete'))) return;
    await this.run(id, () => this.store.remove(id, thread.id, comment.id));
  }

  canDelete(comment: DrawingComment): boolean {
    return comment.text !== null && this.you !== null && canDeleteComment(comment, this.you);
  }

  /**
   * Picks a thread, outlining its box and returning the view to it, at the zoom it was drawn
   * at unless the box would not fit; picking it again lets go.
   */
  select(thread: CommentThread): void {
    const picked = this.selectedId !== thread.id;
    this.selectedId = picked ? thread.id : null;
    this.editor.showRegion(picked ? thread.region : null, thread.zoom);
  }

  /** Runs a request for drawing `id`, keeping its answer only while that drawing is open. */
  private async run(id: string, request: () => Promise<Comments>): Promise<boolean> {
    try {
      const { threads, you } = await request();
      if (id !== this.drawingId) return false;
      this.threads = threads;
      this.you = you;
      this.problem = null;
      if (this.selectedId && !threads.some((thread) => thread.id === this.selectedId)) {
        this.selectedId = null;
        this.editor.showRegion(null);
      }
      return true;
    } catch {
      if (id === this.drawingId) this.problem = FAILED;
      return false;
    }
  }
}

const KEY = Symbol('comments');

/** Hands the session to the Comments panel, which a panel section gets only the editor for. */
export const provideComments = (comments: CommentSession) => setContext(KEY, comments);
export const useComments = (): CommentSession => getContext<CommentSession>(KEY);
